import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, ChevronDown, Menu, X } from "lucide-react";
import EncoreLogo from "@/components/EncoreLogo";
import { SERVICES, PHONE_DISPLAY, PHONE_TEL } from "@/lib/siteContent";
import { captureUtm } from "@/lib/utm";
import { useFeatureFlags } from "@/lib/featureFlags";

export default function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [svcOpen, setSvcOpen] = useState(false);
  const helocEnabled = !!useFeatureFlags()?.heloc_enabled;

  useEffect(() => { captureUtm(); }, []);

  return (
    <header
      className="sticky top-0 z-40"
      style={{ background: "rgba(255,255,255,0.82)", backdropFilter: "blur(16px)", borderBottom: "1px solid var(--border)" }}
      data-testid="site-header"
    >
      <div className="px-4 sm:px-6 md:px-10 lg:px-12 py-3.5 max-w-[1280px] mx-auto flex items-center justify-between gap-6">
        <Link to="/" data-testid="site-logo-link"><EncoreLogo /></Link>

        <nav className="hidden lg:flex items-center gap-7 text-sm" style={{ color: "#000" }}>
          <div className="relative group">
            <button className="inline-flex items-center gap-1 py-2 transition-colors hover:text-[var(--primary)]" data-testid="site-nav-services">
              Financing <ChevronDown size={14} strokeWidth={1.5} />
            </button>
            <div className="absolute left-0 top-full pt-1 hidden group-hover:block">
              <div className="w-72 p-2" style={{ background: "#fff", border: "1px solid var(--border)", borderRadius: 4, boxShadow: "0 12px 32px rgba(14,21,37,0.1)" }}>
                {SERVICES.map((s) => (
                  <Link key={s.slug} to={`/services/${s.slug}`}
                        className="block px-3 py-2 text-sm transition-colors hover:bg-[var(--muted)] rounded-[3px]"
                        data-testid={`site-nav-${s.slug}`}>
                    {s.name}
                  </Link>
                ))}
                {helocEnabled && (
                  <Link to="/heloc"
                        className="block px-3 py-2 text-sm transition-colors hover:bg-[var(--muted)] rounded-[3px]"
                        data-testid="site-nav-heloc">
                    HELOC — Home Equity Line of Credit
                  </Link>
                )}
              </div>
            </div>
          </div>
          <a href="/#process" className="py-2 transition-colors hover:text-[var(--primary)]" data-testid="site-nav-process">How it works</a>
          <Link to="/blog" className="py-2 transition-colors hover:text-[var(--primary)]" data-testid="site-nav-blog">Blog</Link>
          <Link to="/get-funded" className="py-2 transition-colors hover:text-[var(--primary)]" data-testid="site-nav-get-funded">Get funded</Link>
          <Link to="/contact" className="py-2 transition-colors hover:text-[var(--primary)]" data-testid="site-nav-contact">Contact</Link>
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <a href={PHONE_TEL} className="inline-flex items-center gap-1.5 text-sm font-medium" style={{ color: "var(--ink)" }} data-testid="site-phone-link">
            <Phone size={14} strokeWidth={1.5} /> {PHONE_DISPLAY}
          </a>
          <Link to="/apply" className="btn-primary text-sm" data-testid="site-apply-btn">Apply now</Link>
          <Link to="/login" className="text-xs" style={{ color: "var(--muted-fg)" }} data-testid="site-login-link">Broker / staff sign in</Link>
        </div>

        <button className="lg:hidden p-2" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="site-mobile-menu-btn">
          {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden px-4 pb-5 pt-1" style={{ background: "#fff", borderTop: "1px solid var(--border)" }} data-testid="site-mobile-menu">
          <button onClick={() => setSvcOpen(!svcOpen)}
                  className="w-full flex items-center justify-between py-3 text-sm font-medium" style={{ color: "var(--ink)" }}>
            Financing <ChevronDown size={14} style={{ transform: svcOpen ? "rotate(180deg)" : "none" }} />
          </button>
          {svcOpen && SERVICES.map((s) => (
            <Link key={s.slug} to={`/services/${s.slug}`} onClick={() => setOpen(false)}
                  className="block py-2 pl-4 text-sm" style={{ color: "var(--body)" }}>
              {s.name}
            </Link>
          ))}
          {svcOpen && helocEnabled && (
            <Link to="/heloc" onClick={() => setOpen(false)}
                  className="block py-2 pl-4 text-sm" style={{ color: "var(--body)" }}
                  data-testid="site-mobile-nav-heloc">
              HELOC — Home Equity Line of Credit
            </Link>
          )}
          <a href="/#process" onClick={() => setOpen(false)} className="block py-3 text-sm font-medium" style={{ color: "var(--ink)" }}>How it works</a>
          <Link to="/blog" onClick={() => setOpen(false)} className="block py-3 text-sm font-medium" style={{ color: "var(--ink)" }} data-testid="site-mobile-nav-blog">Blog</Link>
          <Link to="/get-funded" onClick={() => setOpen(false)} className="block py-3 text-sm font-medium" style={{ color: "var(--ink)" }} data-testid="site-mobile-get-funded">Get funded</Link>
          <Link to="/contact" onClick={() => setOpen(false)} className="block py-3 text-sm font-medium" style={{ color: "var(--ink)" }}>Contact</Link>
          <div className="flex items-center gap-3 mt-3">
            <Link to="/apply" className="btn-primary text-sm flex-1 justify-center" data-testid="site-mobile-apply-btn">Apply now</Link>
            <a href={PHONE_TEL} className="btn-ghost text-sm inline-flex items-center gap-1.5"><Phone size={14} /> Call</a>
          </div>
          <Link to="/login" className="block mt-4 text-xs" style={{ color: "var(--muted-fg)" }}>Broker / staff sign in</Link>
        </div>
      )}
    </header>
  );
}
